// src/context/AuthContext.tsx
import {
  AuthSession,
  confirmResetPassword as awsConfirmResetPassword,
  confirmSignUp as awsConfirmSignUp,
  getCurrentUser as awsGetCurrentUser,
  resetPassword as awsResetPassword,
  signIn as awsSignIn,
  signOut as awsSignOut,
  fetchAuthSession,
  updatePassword as awsUpdatePassword,
  confirmSignIn as awsConfirmSignIn,
} from "aws-amplify/auth";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useApi } from "./ApiContext";
import { useApp } from "./AppContext";
import { ClinicDetailsResponse } from "../pages/ManageClinic/types";
import { ClinicListResponse, UserRole } from "../types";

export type TokensType = {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
};

interface AuthContextType {
  user: any;
  updateUserConfig: (config: any, onSuccess: () => void, onError: (error: any) => void) => void;
  session: AuthSession | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (
    username: string,
    password: string,
    email: string
  ) => Promise<{ userId: string } | undefined>;
  resetPassword: (username: string) => Promise<void>;
  confirmResetPassword: (
    username: string,
    code: string,
    newPassword: string
  ) => Promise<void>;
  checkUser: () => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  confirmSignIn: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const { apiService, setLoading, setSubmitting } = useApi();
  const { user, setUser, setClinics } = useApp();

  const refreshSession = useCallback(async () => {
    try {
      console.log("Refreshing session...");
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        throw new Error("No valid ID token found");
      }

      apiService.setAccessToken(session.tokens.idToken.toString());
      setSession(session);
    } catch (error) {
      console.error("Error refreshing session", error);
      setUser(null);
      setSession(null);
    }
  }, [apiService]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshSession();
    }, 50 * 60 * 1000);

    return () => clearInterval(interval);
  }, [refreshSession]);

  const checkUser = async () => {
    try {
      console.log("Checking user");
      const currentUser = await awsGetCurrentUser();
      console.log("currentUser", currentUser);
      const session = await fetchAuthSession();

      if (!session.tokens?.idToken) {
        console.warn("User is not authenticated");
        return;
      }

      apiService.setAccessToken(session.tokens.idToken.toString());
      const userResponse: any = await apiService.get("/user");
      console.log("userResponse", userResponse);

      const clinicResponse: ClinicDetailsResponse = await apiService.get(
        "/clinic"
      );

      const { clinic, email, name, config, status, role } = userResponse;
      setUser({
        ...currentUser,
        clinic,
        email,
        name,
        config,
        status,
        role,
        clinicName: clinicResponse.name,
      });

      if (role === UserRole.SuperAdmin) {
        try {
          const clinicsResponse: ClinicListResponse = await apiService.get(
            "/clinics"
          );
          setClinics(clinicsResponse.clinics ?? []);
        } catch (error) {
          console.error("Error loading clinics for super admin", error);
        }
      }

      setSession(session);
    } catch (error) {
      console.warn("User not authenticated:", error);
      setUser(null);
      setSession(null);
    }
  };

  const signIn = async (username: string, password: string) => {
    try {
      setLoading(true);
      const signInResponse = await awsSignIn({ username, password });
      if (
        signInResponse.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        const error = new Error("User needs to set a new password");
        (error as any).name = "UserNeedsNewPassword";
        (error as any).username = username;
        throw error;
      }
      console.log("signInResponse", signInResponse);
      if (!signInResponse.isSignedIn) {
        console.error("Error signing in");
        throw new Error("Error signing in");
      }
    } catch (error) {
      console.error("Error signing in", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await awsSignOut();
      setUser(null);
    } catch (error) {
      console.error("Error signing out", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (
    username: string,
    password: string,
    email: string
  ): Promise<{ userId: string } | undefined> => {
    try {
      setLoading(true);
      const response = await apiService.put("/user", {
        username,
        email,
        password,
      });

      console.log("signup response", response);
    } catch (error) {
      console.error("Error signing up", error);
      throw error;
    }
    return { userId: "1" }; // @todo: fix this
  };

  const resetPassword = async (username: string) => {
    try {
      setLoading(true);
      await awsResetPassword({ username });
    } catch (error) {
      console.error("Error resetting password", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const confirmResetPassword = async (
    username: string,
    confirmationCode: string,
    newPassword: string
  ) => {
    try {
      setLoading(true);
      await awsConfirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    } catch (error) {
      console.error("Error confirming reset password", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await awsUpdatePassword({ oldPassword, newPassword });
    } catch (error) {
      console.error("Error changing password", error);
      throw error;
    }
  };

  const confirmSignUp = async (username: string, confirmationCode: string) => {
    try {
      setLoading(true);
      await awsConfirmSignUp({ username, confirmationCode });
    } catch (error) {
      console.error("Error confirming email", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const confirmSignIn = async (newPassword: string) => {
    const response = awsConfirmSignIn({
      challengeResponse: newPassword,
    });

    console.log("confirmSignIn response", response);
    if (!(await response).isSignedIn) {
      throw new Error("Error signing in");
    }
  };

  const updateUserConfig = useCallback((config: any, onSuccess: () => void, onError: (error: any) => void) => {
    setSubmitting(true);
    apiService
      .post("/user", {
        config: JSON.stringify(config),
      })
      .then(() => {
        setUser({...user!, config});
        onSuccess();
      })
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        updateUserConfig,
        session,
        signIn,
        signOut,
        signUp,
        resetPassword,
        confirmResetPassword,
        checkUser,
        confirmSignUp,
        refreshSession,
        updatePassword,
        confirmSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
